import { useEffect, useState, type FormEvent } from 'react';
import { Carte, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { useReglerLinkedin, type VueMarque } from '../../lib/useParametrage';
import { CLASSE_BOUTON_ENREGISTRER, messageErreur } from './commun';

const CLASSE_BOUTON_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50';

/**
 * Page LinkedIn de l'organisme (TF-455) : l'émetteur des certificats de
 * réalisation que les apprenants ajoutent à leur profil. Réglée, LinkedIn
 * affiche le logo de la page et y renvoie ; sans elle, il reçoit le nom
 * affiché de la marque.
 */
export function SectionLinkedin({ linkedin, nom }: { linkedin: VueMarque['linkedin']; nom: string }): JSX.Element {
  const toast = useToast();
  const regler = useReglerLinkedin();
  const [saisie, setSaisie] = useState(linkedin?.organisationId ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => setSaisie(linkedin?.organisationId ?? ''), [linkedin?.organisationId]);

  const enregistrer = (valeur: string, message: string): void => {
    setErreur(null);
    regler.mutate(valeur, {
      onSuccess: () => toast(message),
      onError: (refus) => setErreur(messageErreur(refus, 'La page LinkedIn n’a pas pu être réglée.')),
    });
  };

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    enregistrer(saisie, 'Page LinkedIn enregistrée');
  };

  return (
    <Carte titre="Page LinkedIn de l’organisme">
      <div className="space-y-4 px-4 py-4 sm:px-6">
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          <div className="min-w-0 rounded-xl bg-codialis-sand p-3">
            <div className="text-[11px] uppercase tracking-wide text-codialis-gray">Émetteur des certificats sur LinkedIn</div>
            {linkedin ? (
              <a
                href={linkedin.page}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 block break-all font-mono text-sm font-semibold text-codialis-blue hover:underline"
              >
                {linkedin.page}
              </a>
            ) : (
              <div className="mt-1 text-sm font-semibold text-codialis-navy">{nom}</div>
            )}
            <div className="mt-1 text-[11px] text-codialis-gray">
              {linkedin
                ? 'Le certificat ajouté au profil d’un apprenant porte le logo de cette page et y renvoie.'
                : 'Aucune page réglée : LinkedIn reçoit le nom affiché, sans logo ni lien.'}
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-gray-100 p-3 text-xs text-codialis-gray">
            <div className="font-semibold text-codialis-navy">Trouver l’identifiant</div>
            <p className="mt-1">
              Ouvrir la page entreprise en tant qu’administrateur : son adresse se termine par{' '}
              <span className="break-all font-mono text-codialis-navy">/company/12345678/admin/</span>. Coller cette
              adresse ou le seul nombre. L’adresse publique à nom (/company/top-formation/) ne le donne pas.
            </p>
          </div>
        </div>

        <form onSubmit={envoyer} className="flex flex-col gap-2 md:flex-row md:items-end">
          <div className="min-w-0 flex-1">
            <label htmlFor="marque-linkedin" className="text-xs font-semibold text-codialis-navy">
              Identifiant ou adresse de la page
            </label>
            <input
              id="marque-linkedin"
              type="text"
              inputMode="url"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="12345678"
              value={saisie}
              onChange={(evenement) => {
                setErreur(null);
                setSaisie(evenement.target.value);
              }}
              className="mt-1 min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {linkedin && (
              <button
                type="button"
                className={CLASSE_BOUTON_SECONDAIRE}
                disabled={regler.isPending}
                onClick={() => enregistrer('', 'Page LinkedIn retirée : LinkedIn reçoit le nom affiché')}
              >
                Retirer
              </button>
            )}
            <button
              type="submit"
              className={CLASSE_BOUTON_ENREGISTRER}
              disabled={regler.isPending || !saisie.trim() || saisie.trim() === linkedin?.organisationId}
            >
              {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>

        {erreur && <MessageErreur message={erreur} />}
      </div>
    </Carte>
  );
}
